/**
 * Staff and shared accounts can log in with a global username (no inbox).
 * Firebase Auth still requires an email-shaped identifier; we map usernames to a
 * stable synthetic address on this domain.
 */
export const SYNTHETIC_AUTH_EMAIL_DOMAIN = 'complin.staff.local';

const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_-]{1,62}[a-z0-9]$/;
const USERNAME_MIN = 3;
const USERNAME_MAX = 64;

export function normalizeStaffUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

export function getStaffUsernameError(normalized: string): string | null {
  if (!normalized) {
    return 'Username is required';
  }
  if (normalized.length < USERNAME_MIN || normalized.length > USERNAME_MAX) {
    return `Username must be between ${USERNAME_MIN} and ${USERNAME_MAX} characters`;
  }
  if (!USERNAME_PATTERN.test(normalized)) {
    return 'Username may only contain letters, numbers, hyphens, and underscores, and must start and end with a letter or number';
  }
  return null;
}

export function usernameToAuthEmail(normalizedUsername: string): string {
  return `${normalizedUsername}@${SYNTHETIC_AUTH_EMAIL_DOMAIN}`;
}

export function isSyntheticStaffAuthEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(`@${SYNTHETIC_AUTH_EMAIL_DOMAIN}`);
}

/** What to show in UI instead of the full synthetic email */
export function formatLoginIdentifierForDisplay(storedEmail: string | null | undefined): string {
  if (!storedEmail) return '';
  if (isSyntheticStaffAuthEmail(storedEmail)) {
    return storedEmail.split('@')[0] ?? storedEmail;
  }
  return storedEmail;
}

function isProbablyEmail(value: string): boolean {
  return value.includes('@');
}

function basicEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Resolves an invite/login field: real email, or username → synthetic email.
 */
export function normalizeInviteIdentifier(input: string): { authEmail?: string; error: string | null } {
  const trimmed = input.trim();
  if (!trimmed) {
    return { error: 'Email or username is required' };
  }
  if (isProbablyEmail(trimmed)) {
    if (!basicEmailValid(trimmed)) {
      return { error: 'Invalid email address' };
    }
    return { authEmail: trimmed.toLowerCase(), error: null };
  }
  const normalized = normalizeStaffUsername(trimmed);
  const err = getStaffUsernameError(normalized);
  if (err) {
    return { error: err };
  }
  return { authEmail: usernameToAuthEmail(normalized), error: null };
}

/**
 * Same rules as invitations; used on the login screen for password sign-in.
 */
export function resolveLoginIdentifier(input: string): { authEmail?: string; error: string | null } {
  return normalizeInviteIdentifier(input);
}
